'use client'

import { createContext, useContext, useMemo } from 'react'
import { useMachine } from '@xstate/react'
import { crmSyncCdrMachine } from './crm-sync-cdr-machine'
import type { CrmSyncCdrRecord } from './crm-sync-cdr-machine'
import {
  crmSyncUserMachine,
  crmSyncBusinessMachine,
  crmSyncAudioMachine,
  crmSyncBillingMachine,
  crmSyncAccessMachine,
  crmSyncCallFlowMachine,
  type CrmUser,
  type CrmBusinessSettings,
  type CrmAudioFile,
  type BillingInvoice,
  type AccessRole,
  type AccessPermission,
  type CrmCallFlow,
} from './crm-sync-machines'

// CDR
type CrmSyncCdrMachineApi = ReturnType<typeof useMachine<typeof crmSyncCdrMachine>>
const CrmSyncCdrMachineContext = createContext<CrmSyncCdrMachineApi | null>(null)

export function CrmSyncCdrMachineProvider({ children }: { children: React.ReactNode }) {
  const api = useMachine(crmSyncCdrMachine)
  return (
    <CrmSyncCdrMachineContext.Provider value={api}>{children}</CrmSyncCdrMachineContext.Provider>
  )
}

export function useCrmSyncCdrMachine() {
  const api = useContext(CrmSyncCdrMachineContext)
  if (!api) throw new Error('useCrmSyncCdrMachine must be used within CrmSyncCdrMachineProvider')
  return api
}

export function useCrmSyncCdrActions() {
  const [, send] = useCrmSyncCdrMachine()
  return useMemo(() => ({
    setRecords: (records: CrmSyncCdrRecord[], total?: number) =>
      send({ type: 'SET_RECORDS', records, total }),
    addRecord: (record: CrmSyncCdrRecord) => send({ type: 'ADD_RECORD', record }),
    setLoading: (loading: boolean) => send({ type: 'SET_LOADING', loading }),
  }), [send])
}

export function useCrmSyncCdrStoreCompat() {
  const machineApi = useCrmSyncCdrMachine()
  const actions = useCrmSyncCdrActions()
  return useMemo(() => {
    const ctx = machineApi[0].context
    return {
      records: ctx.records,
      isLoading: ctx.isLoading,
      totalCount: ctx.totalCount,
      ...actions,
    }
  }, [machineApi, actions])
}

// User Management
const CrmSyncUserContext = createContext<ReturnType<
  typeof useMachine<typeof crmSyncUserMachine>
> | null>(null)
export function CrmSyncUserMachineProvider({ children }: { children: React.ReactNode }) {
  return (
    <CrmSyncUserContext.Provider value={useMachine(crmSyncUserMachine)}>
      {children}
    </CrmSyncUserContext.Provider>
  )
}
export function useCrmSyncUserMachine() {
  const api = useContext(CrmSyncUserContext)
  if (!api) throw new Error('useCrmSyncUserMachine must be used within CrmSyncUserMachineProvider')
  return api
}

export function useCrmSyncUserActions() {
  const [, send] = useCrmSyncUserMachine()
  return useMemo(() => ({
    setUsers: (u: CrmUser[]) => send({ type: 'SET_USERS', users: u }),
    addUser: (u: CrmUser) => send({ type: 'ADD_USER', user: u }),
    updateUser: (id: string, u: Partial<CrmUser>) =>
      send({ type: 'UPDATE_USER', id, updates: u }),
    removeUser: (id: string) => send({ type: 'REMOVE_USER', id }),
    setLoading: (l: boolean) => send({ type: 'SET_LOADING', loading: l }),
  }), [send])
}

export function useCrmSyncUserStoreCompat() {
  const api = useCrmSyncUserMachine()
  const actions = useCrmSyncUserActions()
  return useMemo(
    () => ({
      users: api[0].context.users,
      isLoading: api[0].context.isLoading,
      ...actions,
    }),
    [api, actions]
  )
}

// Business Settings
const CrmSyncBusinessContext = createContext<ReturnType<
  typeof useMachine<typeof crmSyncBusinessMachine>
> | null>(null)
export function CrmSyncBusinessMachineProvider({ children }: { children: React.ReactNode }) {
  return (
    <CrmSyncBusinessContext.Provider value={useMachine(crmSyncBusinessMachine)}>
      {children}
    </CrmSyncBusinessContext.Provider>
  )
}
export function useCrmSyncBusinessMachine() {
  const api = useContext(CrmSyncBusinessContext)
  if (!api) throw new Error('useCrmSyncBusinessMachine must be used within CrmSyncBusinessMachineProvider')
  return api
}
export function useCrmSyncBusinessActions() {
  const [, send] = useCrmSyncBusinessMachine()
  return useMemo(() => ({
    setSettings: (s: CrmBusinessSettings) => send({ type: 'SET_SETTINGS', settings: s }),
    updateSettings: (u: Partial<CrmBusinessSettings>) =>
      send({ type: 'UPDATE_SETTINGS', updates: u }),
    setLoading: (l: boolean) => send({ type: 'SET_LOADING', loading: l }),
  }), [send])
}

export function useCrmSyncBusinessStoreCompat() {
  const api = useCrmSyncBusinessMachine()
  const actions = useCrmSyncBusinessActions()
  return useMemo(
    () => ({
      settings: api[0].context.settings,
      isLoading: api[0].context.isLoading,
      ...actions,
    }),
    [api, actions]
  )
}

// Audio File
const CrmSyncAudioContext = createContext<ReturnType<
  typeof useMachine<typeof crmSyncAudioMachine>
> | null>(null)
export function CrmSyncAudioMachineProvider({ children }: { children: React.ReactNode }) {
  return (
    <CrmSyncAudioContext.Provider value={useMachine(crmSyncAudioMachine)}>
      {children}
    </CrmSyncAudioContext.Provider>
  )
}
export function useCrmSyncAudioMachine() {
  const api = useContext(CrmSyncAudioContext)
  if (!api) throw new Error('useCrmSyncAudioMachine must be used within CrmSyncAudioMachineProvider')
  return api
}
export function useCrmSyncAudioActions() {
  const [, send] = useCrmSyncAudioMachine()
  return useMemo(() => ({
    setAudioFiles: (f: CrmAudioFile[]) => send({ type: 'SET_FILES', files: f }),
    addAudioFile: (f: CrmAudioFile) => send({ type: 'ADD_FILE', file: f }),
    updateAudioFile: (id: string, u: Partial<CrmAudioFile>) =>
      send({ type: 'UPDATE_FILE', id, updates: u }),
    removeAudioFile: (id: string) => send({ type: 'REMOVE_FILE', id }),
    setLoading: (l: boolean) => send({ type: 'SET_LOADING', loading: l }),
  }), [send])
}

export function useCrmSyncAudioStoreCompat() {
  const api = useCrmSyncAudioMachine()
  const actions = useCrmSyncAudioActions()
  return useMemo(
    () => ({
      audioFiles: api[0].context.audioFiles,
      isLoading: api[0].context.isLoading,
      ...actions,
    }),
    [api, actions]
  )
}

// Billing
const CrmSyncBillingContext = createContext<ReturnType<
  typeof useMachine<typeof crmSyncBillingMachine>
> | null>(null)
export function CrmSyncBillingMachineProvider({ children }: { children: React.ReactNode }) {
  return (
    <CrmSyncBillingContext.Provider value={useMachine(crmSyncBillingMachine)}>
      {children}
    </CrmSyncBillingContext.Provider>
  )
}
export function useCrmSyncBillingMachine() {
  const api = useContext(CrmSyncBillingContext)
  if (!api) throw new Error('useCrmSyncBillingMachine must be used within CrmSyncBillingMachineProvider')
  return api
}
export function useCrmSyncBillingActions() {
  const [, send] = useCrmSyncBillingMachine()
  return useMemo(() => ({
    setInvoices: (i: BillingInvoice[]) => send({ type: 'SET_INVOICES', invoices: i }),
    addInvoice: (i: BillingInvoice) => send({ type: 'ADD_INVOICE', invoice: i }),
    updateInvoice: (id: string, u: Partial<BillingInvoice>) =>
      send({ type: 'UPDATE_INVOICE', id, updates: u }),
    removeInvoice: (id: string) => send({ type: 'REMOVE_INVOICE', id }),
    setStats: (s: any) => send({ type: 'SET_STATS', stats: s }),
    setLoading: (l: boolean) => send({ type: 'SET_LOADING', loading: l }),
  }), [send])
}

export function useCrmSyncBillingStoreCompat() {
  const api = useCrmSyncBillingMachine()
  const actions = useCrmSyncBillingActions()
  return useMemo(
    () => ({
      invoices: api[0].context.invoices,
      isLoading: api[0].context.isLoading,
      stats: api[0].context.stats,
      ...actions,
    }),
    [api, actions]
  )
}

// Access
const CrmSyncAccessContext = createContext<ReturnType<
  typeof useMachine<typeof crmSyncAccessMachine>
> | null>(null)
export function CrmSyncAccessMachineProvider({ children }: { children: React.ReactNode }) {
  return (
    <CrmSyncAccessContext.Provider value={useMachine(crmSyncAccessMachine)}>
      {children}
    </CrmSyncAccessContext.Provider>
  )
}
export function useCrmSyncAccessMachine() {
  const api = useContext(CrmSyncAccessContext)
  if (!api) throw new Error('useCrmSyncAccessMachine must be used within CrmSyncAccessMachineProvider')
  return api
}
export function useCrmSyncAccessActions() {
  const [, send] = useCrmSyncAccessMachine()
  return useMemo(() => ({
    setRoles: (r: AccessRole[]) => send({ type: 'SET_ROLES', roles: r }),
    addRole: (r: AccessRole) => send({ type: 'ADD_ROLE', role: r }),
    updateRole: (id: string, u: Partial<AccessRole>) =>
      send({ type: 'UPDATE_ROLE', id, updates: u }),
    removeRole: (id: string) => send({ type: 'REMOVE_ROLE', id }),
    setPermissions: (p: AccessPermission[]) =>
      send({ type: 'SET_PERMISSIONS', permissions: p }),
    setLoading: (l: boolean) => send({ type: 'SET_LOADING', loading: l }),
  }), [send])
}

export function useCrmSyncAccessStoreCompat() {
  const api = useCrmSyncAccessMachine()
  const actions = useCrmSyncAccessActions()
  return useMemo(
    () => ({
      roles: api[0].context.roles,
      permissions: api[0].context.permissions,
      isLoading: api[0].context.isLoading,
      ...actions,
    }),
    [api, actions]
  )
}

// Call Flow
const CrmSyncCallFlowContext = createContext<ReturnType<
  typeof useMachine<typeof crmSyncCallFlowMachine>
> | null>(null)
export function CrmSyncCallFlowMachineProvider({ children }: { children: React.ReactNode }) {
  return (
    <CrmSyncCallFlowContext.Provider value={useMachine(crmSyncCallFlowMachine)}>
      {children}
    </CrmSyncCallFlowContext.Provider>
  )
}
export function useCrmSyncCallFlowMachine() {
  const api = useContext(CrmSyncCallFlowContext)
  if (!api) throw new Error('useCrmSyncCallFlowMachine must be used within CrmSyncCallFlowMachineProvider')
  return api
}
export function useCrmSyncCallFlowActions() {
  const [, send] = useCrmSyncCallFlowMachine()
  return useMemo(() => ({
    setCallFlows: (f: CrmCallFlow[]) => send({ type: 'SET_FLOWS', flows: f }),
    addCallFlow: (f: CrmCallFlow) => send({ type: 'ADD_FLOW', flow: f }),
    updateCallFlow: (id: string, u: Partial<CrmCallFlow>) =>
      send({ type: 'UPDATE_FLOW', id, updates: u }),
    removeCallFlow: (id: string) => send({ type: 'REMOVE_FLOW', id }),
    setLoading: (l: boolean) => send({ type: 'SET_LOADING', loading: l }),
  }), [send])
}

export function useCrmSyncCallFlowStoreCompat() {
  const api = useCrmSyncCallFlowMachine()
  const actions = useCrmSyncCallFlowActions()
  return useMemo(
    () => ({
      callFlows: api[0].context.callFlows,
      isLoading: api[0].context.isLoading,
      ...actions,
    }),
    [api, actions]
  )
}

// Combined provider for admin layout
export function CrmSyncMachinesProvider({ children }: { children: React.ReactNode }) {
  return (
    <CrmSyncCdrMachineProvider>
      <CrmSyncUserMachineProvider>
        <CrmSyncBusinessMachineProvider>
          <CrmSyncAudioMachineProvider>
            <CrmSyncBillingMachineProvider>
              <CrmSyncAccessMachineProvider>
                <CrmSyncCallFlowMachineProvider>{children}</CrmSyncCallFlowMachineProvider>
              </CrmSyncAccessMachineProvider>
            </CrmSyncBillingMachineProvider>
          </CrmSyncAudioMachineProvider>
        </CrmSyncBusinessMachineProvider>
      </CrmSyncUserMachineProvider>
    </CrmSyncCdrMachineProvider>
  )
}
